import "./dislike_reason_popover.scss";
import { useRef, useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { MultiCheckbox, type Option } from "@sprout/checkbox";
import { TextInput } from "@sprout/text_input.tsx";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import useTranslation from "@/hooks/use_translation";

export const COMMENT_MAX_LENGTH = 500;

const REASON_VALUES = ["job_title", "seniority_level", "location", "work_type"] as const;

const REASON_I18N_KEY: Record<string, string> = {
  job_title: "jobTitle",
  seniority_level: "seniorityLevel",
  location: "location",
  work_type: "workType",
};

export type DislikeReason = {
  reasons: string[];
  comment: string;
};

type Props = {
  open: boolean;
  onDismiss: () => void;
  onSubmit: (reason: DislikeReason) => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
};

const DislikeReasonForm = ({ onDismiss, onSubmit }: Pick<Props, "onDismiss" | "onSubmit">) => {
  const { t } = useTranslation("jobSearch");
  const [reasons, setReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");

  const options: Option[] = REASON_VALUES.map((value) => ({
    value,
    label: t(`recommendationFeedback.dislikeReason.options.${REASON_I18N_KEY[value]}`),
  }));

  return (
    <div className="dislike-reason flex flex-col">
      <p className="font-medium text-base leading-6 text-[var(--text-primary,#15372c)] mb-2">
        {t("recommendationFeedback.dislikeReason.title")}
      </p>

      <MultiCheckbox
        id="dislike-reasons"
        isMulti
        spacing={3}
        description={t("recommendationFeedback.dislikeReason.subtitle")}
        options={options}
        values={reasons}
        onSelect={(selected) => setReasons((selected as Option[]).map((option) => option.value))}
      />

      <div className="mt-6 mb-4">
        <TextInput
          id="dislike-comment"
          label={null}
          placeholder={t("recommendationFeedback.dislikeReason.commentLabel")}
          value={comment}
          onChange={(event) => setComment(event.currentTarget.value)}
          multiline
          rows={5}
          maxLength={COMMENT_MAX_LENGTH}
        />
        <p id="dislike-comment-help" className="helper-text">
          {t("recommendationFeedback.dislikeReason.characterCount", {
            used: comment.length,
            max: COMMENT_MAX_LENGTH,
          })}
        </p>
        <p className="sr-only" role="status">
          {comment.length >= COMMENT_MAX_LENGTH
            ? t("recommendationFeedback.dislikeReason.characterLimitReached", {
                max: COMMENT_MAX_LENGTH,
              })
            : ""}
        </p>
      </div>

      <div className="flex gap-2 justify-end">
        <SecondaryButton size="medium" onClick={onDismiss}>
          <span className="whitespace-nowrap">
            {t("recommendationFeedback.dislikeReason.cancel")}
          </span>
        </SecondaryButton>
        <PrimaryButton size="medium" onClick={() => onSubmit({ reasons, comment })}>
          <span className="whitespace-nowrap">
            {t("recommendationFeedback.dislikeReason.submit")}
          </span>
        </PrimaryButton>
      </div>
    </div>
  );
};

const DislikeReasonPopover = ({ open, onDismiss, onSubmit, triggerRef, children }: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <PopoverAnchor ref={anchorRef}>{children}</PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={6}
        collisionPadding={8}
        className="w-[min(22rem,calc(100vw-1rem))] p-6"
        onClick={(event) => event.stopPropagation()}
        onCloseAutoFocus={(event) => {
          if (!triggerRef?.current) return;
          event.preventDefault();
          triggerRef.current.focus();
        }}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
      >
        <DislikeReasonForm onDismiss={onDismiss} onSubmit={onSubmit} />
      </PopoverContent>
    </Popover>
  );
};

export default DislikeReasonPopover;
